// Admin logo manager slice. Owns the partner/agency logo list shown in the
// Branding page: load, upload (raster or SVG → normalized PNG server-side),
// edit alt/href/role, reorder (drag), and delete. Bytes live in Postgres; this
// slice only ever holds metadata + the server-issued ids. A dead session bounces
// to the login screen via clearAdminSession, mirroring the product editor.
import { createSlice } from '@reduxjs/toolkit';
import { adminFetch, AdminAuthError } from '../api/admin-client.js';
import { clearAdminSession } from './admin-auth-slice.js';
import { fetchPublicConfig } from './settings-slice.js';

// Client-side guards mirroring the server (services/logo-image.js +
// middleware/upload-logo.js) so a bad file fails fast with a clear message.
export const LOGO_MAX_BYTES = 5 * 1024 * 1024;
export const LOGO_MAX_COUNT = 4;
export const ALLOWED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'];

const base = '/api/admin/logos';

const initialState = {
  items: [],
  loaded: false,
  loading: false,
  error: null,
  busy: false,
  actionError: null,
};

const adminLogosSlice = createSlice({
  name: 'adminLogos',
  initialState,
  reducers: {
    setLoading(state, action) { state.loading = action.payload; },
    setError(state, action) { state.error = action.payload; },
    setItems(state, action) {
      state.items = action.payload;
      state.loaded = true;
    },
    setBusy(state, action) { state.busy = action.payload; },
    setActionError(state, action) { state.actionError = action.payload; },
    clearActionError(state) { state.actionError = null; },
  },
});

export const {
  setLoading, setError, setItems, setBusy, setActionError, clearActionError,
} = adminLogosSlice.actions;

// Load the current logo list. Safe to call on mount; cheap (metadata only).
export const fetchAdminLogos = () => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const { items } = await adminFetch(base);
    dispatch(setItems(items));
  } catch (err) {
    if (err instanceof AdminAuthError) dispatch(clearAdminSession());
    dispatch(setError(err.message));
  } finally {
    dispatch(setLoading(false));
  }
};

// Run a mutating request, refresh both the admin list and the public config
// (so the live header/landing/email lockup updates), and surface any error.
const withLogoRequest = (fn) => async (dispatch) => {
  dispatch(setBusy(true));
  dispatch(setActionError(null));
  try {
    await fn(dispatch);
    const { items } = await adminFetch(base);
    dispatch(setItems(items));
    dispatch(fetchPublicConfig());
    return { ok: true };
  } catch (err) {
    if (err instanceof AdminAuthError) dispatch(clearAdminSession());
    dispatch(setActionError(err.message));
    return { ok: false };
  } finally {
    dispatch(setBusy(false));
  }
};

// Upload a new logo. `file` is a File; alt is required, href/role optional.
export const uploadLogo = ({
  file, altText, href, role,
}) => withLogoRequest(async () => {
  const form = new FormData();
  form.append('logo', file);
  form.append('alt_text', altText);
  if (href) form.append('href', href);
  if (role) form.append('role', role);
  await adminFetch(base, { method: 'POST', body: form });
});

// Patch metadata (alt text, click-through, or role) on an existing logo.
export const patchLogo = (id, patch) => withLogoRequest(async () => {
  await adminFetch(`${base}/${id}`, { method: 'PATCH', body: patch });
});

export const deleteLogo = (id) => withLogoRequest(async () => {
  await adminFetch(`${base}/${id}`, { method: 'DELETE' });
});

export const reorderLogos = (orderedIds) => withLogoRequest(async () => {
  await adminFetch(`${base}/order`, { method: 'PUT', body: { order: orderedIds } });
});

export default adminLogosSlice.reducer;
